import { Button } from '@affine/component/ui/button';
import { notify } from '@affine/component/ui/notification';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import { UrlService } from '@affine/core/modules/url';
import { UserFriendlyError } from '@affine/error';
import { OAuthProviderType } from '@affine/graphql';
import track from '@affine/track';
import {
  AppleIcon,
  GithubIcon,
  GoogleIcon,
  LockIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type ReactElement,
  type SVGAttributes,
  useCallback,
  useEffect,
  useRef,
} from 'react';

const OAuthProviderMap: Record<
  OAuthProviderType,
  {
    icon?: ReactElement<SVGAttributes<SVGElement>>;
  }
> = {
  [OAuthProviderType.FydeOS]: {},

  [OAuthProviderType.Google]: {
    icon: <GoogleIcon />,
  },

  [OAuthProviderType.GitHub]: {
    icon: <GithubIcon />,
  },

  [OAuthProviderType.OIDC]: {
    icon: <LockIcon />,
  },

  [OAuthProviderType.Apple]: {
    icon: <AppleIcon />,
  },
};

export function OAuth({
  redirectUrl,
  autoRedirect = false,
}: {
  redirectUrl?: string;
  autoRedirect?: boolean;
}) {
  const serverService = useService(ServerService);
  const urlService = useService(UrlService);
  const oauth = useLiveData(serverService.server.features$.map(r => r?.oauth));
  const oauthProviders = useLiveData(
    serverService.server.config$.map(r => r?.oauthProviders)
  );
  const auth = useService(AuthService);
  const hasStartedAutoRedirect = useRef(false);

  const onContinue = useAsyncCallback(
    async (provider: OAuthProviderType) => {
      track.$.$.auth.signIn({ method: 'oauth', provider });

      const open: () => Promise<void> | void = BUILD_CONFIG.isNative
        ? async () => {
            try {
              const scheme = urlService.getClientScheme();
              const options = await auth.oauthPreflight(
                provider,
                scheme ?? 'web'
              );
              urlService.openPopupWindow(options.url);
            } catch (e) {
              notify.error(UserFriendlyError.fromAny(e));
            }
          }
        : () => {
            const params = new URLSearchParams();

            params.set('provider', provider);

            if (redirectUrl) {
              params.set('redirect_uri', redirectUrl);
            }

            params.set('flow', 'redirect');

            const oauthUrl =
              serverService.server.baseUrl +
              `/oauth/login?${params.toString()}`;

            urlService.openExternal(oauthUrl);
          };

      const ret = open();

      if (ret instanceof Promise) {
        await ret;
      }
    },
    [urlService, redirectUrl, serverService, auth]
  );

  useEffect(() => {
    if (
      !autoRedirect ||
      BUILD_CONFIG.isNative ||
      !oauth ||
      !oauthProviders?.includes(OAuthProviderType.FydeOS) ||
      hasStartedAutoRedirect.current
    ) {
      return;
    }

    hasStartedAutoRedirect.current = true;
    onContinue(OAuthProviderType.FydeOS);
  }, [autoRedirect, oauth, oauthProviders, onContinue]);

  if (!oauth) {
    return null;
  }

  if (autoRedirect && !BUILD_CONFIG.isNative) {
    return null;
  }

  return oauthProviders
    ?.filter(provider =>
      BUILD_CONFIG.isNative
        ? provider !== OAuthProviderType.FydeOS
        : provider === OAuthProviderType.FydeOS
    )
    .map(provider => (
      <OAuthProvider
        key={provider}
        provider={provider}
        onContinue={onContinue}
      />
    ));
}

interface OauthProviderProps {
  provider: OAuthProviderType;
  onContinue: (provider: OAuthProviderType) => void;
}

function OAuthProvider({ onContinue, provider }: OauthProviderProps) {
  const { icon } =
    provider in OAuthProviderMap
      ? OAuthProviderMap[provider]
      : { icon: undefined };

  const onClick = useCallback(() => {
    onContinue(provider);
  }, [onContinue, provider]);

  return (
    <Button
      variant={provider === OAuthProviderType.Apple ? 'custom' : 'primary'}
      block
      size="extraLarge"
      style={{ width: '100%' }}
      prefix={icon}
      onClick={onClick}
    >
      Continue with{' '}
      {provider === OAuthProviderType.FydeOS ? 'FydeOS' : provider}
    </Button>
  );
}
